/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { MULTIPLE_VALUE } from '../../../constants';
import { FONT_REFERENCE, NONE } from '../customConstants';
import {
  getPrefixStylesInSelection,
  togglePrefixStyle,
} from '../styleManipulation';
import { getVariable, isStyle } from './util';
import {
  FONT_REFERENCE_ATTRIBUTE,
  getTextFontRenderFamily,
} from '../../../elements/text/utils/mixedTypography';

const VALID_REFERENCE_ID = /^[A-Za-z0-9_-]{1,128}$/;

export const fontReferenceToStyle = (referenceId) =>
  `${FONT_REFERENCE}-${referenceId}`;

export const styleToFontReference = (style) =>
  getVariable(style, FONT_REFERENCE);

function getReferenceMap(context) {
  return context?.fontReferenceMap || new Map();
}

function getReference(context, referenceId) {
  const referenceMap = getReferenceMap(context);
  return referenceMap instanceof Map
    ? referenceMap.get(referenceId)
    : referenceMap[referenceId];
}

function elementToStyle(element) {
  if (element.tagName.toLowerCase() !== 'span') {
    return null;
  }

  const referenceId = element.getAttribute(FONT_REFERENCE_ATTRIBUTE) || '';
  if (!referenceId || !VALID_REFERENCE_ID.test(referenceId)) {
    return null;
  }

  return fontReferenceToStyle(referenceId);
}

function getReferenceStyle(styles) {
  return styles.find((style) => isStyle(style, FONT_REFERENCE));
}

function stylesToCSS(styles, context) {
  const style = getReferenceStyle(styles);
  if (!style) {
    return null;
  }

  const referenceId = styleToFontReference(style);
  if (!VALID_REFERENCE_ID.test(referenceId)) {
    return null;
  }

  const reference = getReference(context, referenceId);
  if (!reference) {
    return null;
  }

  const fontFamily = getTextFontRenderFamily(reference);
  return fontFamily ? { fontFamily } : null;
}

function stylesToAttributes(styles) {
  const style = getReferenceStyle(styles);
  if (!style) {
    return null;
  }

  const referenceId = styleToFontReference(style);
  return VALID_REFERENCE_ID.test(referenceId)
    ? { [FONT_REFERENCE_ATTRIBUTE]: referenceId }
    : null;
}

function getEffectiveReferenceIds(editorState, context) {
  return getPrefixStylesInSelection(editorState, FONT_REFERENCE).map((style) =>
    style === NONE ? null : styleToFontReference(style)
  );
}

function getFontReferenceId(editorState, context) {
  const referenceIds = new Set(getEffectiveReferenceIds(editorState, context));
  return referenceIds.size > 1 ? MULTIPLE_VALUE : [...referenceIds][0];
}

function getFontFamily(editorState, context) {
  const families = new Set(
    getEffectiveReferenceIds(editorState, context).map((referenceId) => {
      if (!referenceId) {
        return context?.defaultFont?.family;
      }
      return (
        getReference(context, referenceId)?.font?.family ||
        context?.defaultFont?.family
      );
    })
  );

  return families.size > 1 ? MULTIPLE_VALUE : [...families][0];
}

function setFontReference(editorState, referenceId) {
  if (referenceId && !VALID_REFERENCE_ID.test(referenceId)) {
    return editorState;
  }

  const shouldSetStyle = () => Boolean(referenceId);
  const getStyleToSet = () => fontReferenceToStyle(referenceId);

  return togglePrefixStyle(
    editorState,
    FONT_REFERENCE,
    shouldSetStyle,
    getStyleToSet
  );
}

const formatter = {
  elementToStyle,
  stylesToCSS,
  stylesToAttributes,
  autoFocus: true,
  getters: {
    fontReferenceId: getFontReferenceId,
    fontFamily: getFontFamily,
  },
  setters: {
    setFontReference,
  },
};

export default formatter;
